import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, CheckCircle2 } from 'lucide-react';
import type { ReactNode } from 'react';
import type { AudienceMeta } from '@shared/constants/audiences';
import { Container } from '../layout/Container';
import { Button } from '../ui/button';
import { getIcon } from '../../lib/lucide-icons';
import { getAudienceTheme } from '../../config/audience-theme';
import type { AudienceVoice } from '../../config/audience-voice';
import { cn } from '../../lib/cn';
import { easeStripe } from '../../lib/motion';

export interface SectorHeroProps {
  audience: AudienceMeta;
  voice: AudienceVoice;
  /** Slot droit personnalisable (carte preview, illustration custom...) */
  sideSlot?: ReactNode;
  /** Si pas de side slot fourni, on retombe sur "promiseList" auto */
  /**
   * Liste de promesses chiffrées (side panel auto).
   * V4 palette restreinte : tous les tones legacy (lavender/sage) sont
   * neutralisés visuellement vers brand/ink en interne.
   */
  promiseList?: Array<{
    label: string;
    value: string;
    tone?: 'brand' | 'accent' | 'ink' | 'lavender' | 'sage';
  }>;
  /** Si défini, ajoute un toolbar interactif sous le hero (chips d'action rapide) */
  miniActions?: Array<{ label: string; href: string; iconName: string }>;
}

// V4 palette restreinte — lavender/sage legacy mappés vers brand/ink
const PROMISE_TONE_TEXT = {
  brand: 'text-brand-700',
  accent: 'text-accent-700',
  ink: 'text-ink',
  lavender: 'text-brand-700',
  sage: 'text-stone-700',
} as const;

/**
 * SectorHero — hero magazine moderne pour pages d'accueil sectorielles.
 *
 * Caractéristiques 3.0 (Vercel / Linear / Stripe) :
 *  - Background dot-grid masqué + 1 spot glow unique (vs 2 halos blobs)
 *  - Eyebrow chip avec live-dot pulse
 *  - Headline avec gradient audience-specific sur l'accent
 *  - Bullets de réassurance + micro-promesse en pied de hero
 *  - Side slot configurable (preview card style live-card)
 *  - CTA primaire et secondaire issus de audience-voice.ts
 *  - Tags monospace pour mini-actions (Linear-style)
 */
export function SectorHero({
  audience,
  voice,
  sideSlot,
  promiseList,
  miniActions,
}: SectorHeroProps) {
  const theme = getAudienceTheme(audience.id);
  const PrimaryIcon = getIcon(voice.hero.primaryCta.iconName);
  const SecondaryIcon = getIcon(voice.hero.secondaryCta.iconName);

  // Spot glow tone : accent pour pôles phares, brand sinon (palette restreinte V4)
  const spotClass =
    audience.tone === 'accent' ? 'spot-glow spot-glow-accent' : 'spot-glow';

  // Live dot tone : accent pour pôles phares, brand sinon
  const dotClass =
    audience.tone === 'accent' ? 'live-dot live-dot-accent' : 'live-dot';

  return (
    <section className="relative overflow-hidden bg-paper pt-14 pb-20 md:pt-20 md:pb-24">
      {/* Background Vercel/Linear : dot-grid masqué + 1 spot glow */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 dot-grid-masked" />
        <div className={spotClass} />
      </div>

      <Container className="relative">
        <div
          className={cn(
            'grid items-center gap-10 lg:gap-14',
            sideSlot || promiseList ? 'lg:grid-cols-[1.1fr_1fr]' : 'lg:grid-cols-1',
          )}
        >
          {/* ─────────── Colonne TEXTE ─────────── */}
          <div>
            {/* Eyebrow chip Linear */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: easeStripe }}
              className="flex flex-wrap items-center gap-2"
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white/80 px-3 py-1 text-[11px] font-medium text-stone-600 shadow-hairline backdrop-blur">
                <span className={dotClass} aria-hidden />
                <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                  {audience.id.slice(0, 3).toUpperCase()}
                </span>
                <span className="h-3 w-px bg-stone-200" aria-hidden />
                <span>{voice.hero.eyebrow}</span>
              </span>
              <span className="hidden text-[11.5px] text-stone-500 md:inline">
                {voice.hero.promise}
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.06, ease: easeStripe }}
              className="mt-6 max-w-2xl text-balance font-display text-[clamp(2rem,4.5vw,3.25rem)] font-bold leading-[1.04] tracking-[-0.03em] text-ink"
            >
              {voice.hero.headline}{' '}
              <span className={theme.gradientText}>{voice.hero.headlineAccent}</span>
            </motion.h1>

            {/* Sub */}
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.14, ease: easeStripe }}
              className="mt-5 max-w-xl text-[15px] leading-relaxed text-stone-600"
            >
              {voice.hero.subheadline}
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.22, ease: easeStripe }}
              className="mt-7 flex flex-wrap items-center gap-2.5"
            >
              <Button asChild size="lg" variant={theme.primaryButtonVariant} className="group">
                <a href={voice.hero.primaryCta.href}>
                  {voice.hero.primaryCta.iconName && (
                    <PrimaryIcon className="h-3.5 w-3.5" strokeWidth={2} />
                  )}
                  {voice.hero.primaryCta.label}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5" />
                </a>
              </Button>
              <Button asChild size="lg" variant="secondary" className="group">
                <a href={voice.hero.secondaryCta.href}>
                  {voice.hero.secondaryCta.iconName && (
                    <SecondaryIcon className="h-3.5 w-3.5" strokeWidth={2} />
                  )}
                  {voice.hero.secondaryCta.label}
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                </a>
              </Button>
            </motion.div>

            {/* Bullets de réassurance */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.32, ease: easeStripe }}
              className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-stone-600"
            >
              {voice.hero.bullets.map((b) => (
                <span key={b} className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className={cn('h-3 w-3', theme.bulletDot)} strokeWidth={2.2} />
                  {b}
                </span>
              ))}
            </motion.div>

            {/* Micro-promise */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.42, ease: easeStripe }}
              className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] text-stone-500"
            >
              <span className="inline-block h-1 w-1 rounded-full bg-stone-400" />
              {voice.hero.microPromise}
            </motion.p>

            {/* Mini chips actions style tag-mono */}
            {miniActions && miniActions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5, ease: easeStripe }}
                className="mt-7 flex flex-wrap items-center gap-1.5"
              >
                {miniActions.map((action) => {
                  const Icon = getIcon(action.iconName);
                  return (
                    <a
                      key={action.label}
                      href={action.href}
                      className="tag-mono group hover:-translate-y-0.5"
                    >
                      <Icon className="h-3 w-3 shrink-0" strokeWidth={2} />
                      <span>{action.label}</span>
                      <ArrowUpRight
                        className="h-3 w-3 shrink-0 opacity-50 transition-all duration-150 ease-out group-hover:translate-x-0.5 group-hover:opacity-100"
                        strokeWidth={2}
                      />
                    </a>
                  );
                })}
              </motion.div>
            )}
          </div>

          {/* ─────────── Colonne SIDE SLOT ─────────── */}
          {(sideSlot || promiseList) && (
            <motion.div
              initial={{ opacity: 0, scale: 0.97, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.18, ease: easeStripe }}
              className="relative"
            >
              {/* Halo subtil derrière la card */}
              <div
                aria-hidden
                className={cn(
                  'absolute -inset-6 -z-10 rounded-3xl bg-gradient-to-br blur-3xl',
                  theme.sideCardBg,
                )}
              />
              {sideSlot ?? (
                promiseList && (
                  <div className="live-card p-6">
                    {/* Header */}
                    <div className="flex items-center gap-2 border-b border-stone-100 pb-4">
                      <span className={dotClass} aria-hidden />
                      <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                        Aperçu chiffré · {audience.id.slice(0, 3).toUpperCase()}
                      </span>
                    </div>

                    {/* Promise rows */}
                    <ul className="mt-4 space-y-2">
                      {promiseList.map((item, i) => (
                        <motion.li
                          key={item.label}
                          initial={{ opacity: 0, x: -6 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4, delay: 0.3 + i * 0.06, ease: easeStripe }}
                          className="flex items-center justify-between gap-3 rounded-md border border-stone-100 bg-stone-50/40 px-3 py-2.5 transition-colors hover:border-stone-200 hover:bg-stone-50"
                        >
                          <span className="text-[13px] font-medium text-stone-700">{item.label}</span>
                          <span
                            className={cn(
                              'font-mono text-[12px] font-semibold tabular-nums',
                              item.tone ? PROMISE_TONE_TEXT[item.tone] : 'text-ink',
                            )}
                          >
                            {item.value}
                          </span>
                        </motion.li>
                      ))}
                    </ul>

                    {/* Footer */}
                    <p className="mt-4 border-t border-stone-100 pt-3 font-mono text-[11px] text-stone-500">
                      {voice.hero.microPromise}
                    </p>
                  </div>
                )
              )}
            </motion.div>
          )}
        </div>
      </Container>
    </section>
  );
}
